import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api, trackUrl } from "../lib/api";
import StorefrontHeader from "../components/StorefrontHeader";
import Footer from "../components/Footer";
import ProductCard from "../components/ProductCard";
import { platformLabel } from "../lib/platforms";
import { ArrowUpRight, ArrowLeft, Check } from "lucide-react";

export default function ProductDetail() {
  const { id } = useParams();
  const [product, setProduct] = useState(null);
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get(`/products/${id}`).then((r) => {
      setProduct(r.data);
      api.get("/products", { params: { platform: r.data.platform, limit: 4 } }).then((rr) =>
        setRelated(rr.data.filter((x) => x.id !== id).slice(0, 3))
      );
    }).finally(() => setLoading(false));
  }, [id]);

  if (loading) return <div className="min-h-screen flex items-center justify-center">Loading…</div>;
  if (!product) return <div className="min-h-screen flex items-center justify-center">Product not found.</div>;

  return (
    <div className="min-h-screen bg-[#F8F8F7] text-[#0A0A0A]" data-testid="product-detail">
      <StorefrontHeader />

      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 pt-6 pb-20">
        <Link to="/shop" className="inline-flex items-center gap-2 text-sm font-semibold uppercase tracking-wider story-link mb-8" data-testid="back-link">
          <ArrowLeft className="w-4 h-4" /> Back to shop
        </Link>

        <div className="grid md:grid-cols-12 gap-10">
          <div className="md:col-span-7">
            <div className="aspect-[4/5] overflow-hidden bg-[#EBEBEA]">
              <img src={product.image_url} alt={product.title} className="w-full h-full object-cover" />
            </div>
          </div>

          <div className="md:col-span-5">
            <span className="inline-block px-2.5 py-1 bg-[#0A0A0A] text-white text-[10px] uppercase tracking-[0.18em] font-semibold">
              {platformLabel(product.platform)}
            </span>
            <p className="overline text-[#666] mt-6">{product.category}</p>
            <h1 className="font-display font-black text-4xl md:text-5xl lg:text-6xl tracking-tighter mt-3 leading-[0.95]">
              {product.title}
            </h1>
            <p className="text-3xl font-semibold mt-8">
              {product.currency === "USD" ? "$" : product.currency} {product.price.toFixed(2)}
            </p>
            <p className="mt-6 text-base text-[#333] leading-relaxed whitespace-pre-line">{product.description}</p>

            <a
              data-testid="buy-now-cta"
              href={trackUrl(product.id)}
              target="_blank"
              rel="noopener noreferrer sponsored"
              className="mt-10 w-full inline-flex items-center justify-center gap-2 bg-[#E53935] text-white px-7 py-5 text-sm uppercase tracking-wider font-bold hover:bg-[#C62828] transition-colors"
            >
              Buy on {platformLabel(product.platform)} <ArrowUpRight className="w-4 h-4" />
            </a>
            <p className="text-xs text-[#666] mt-3">Affiliate link — we may earn a small commission.</p>

            <ul className="mt-10 space-y-3 border-t border-black/10 pt-8">
              {(product.tags?.length ? product.tags : ["Hand-picked", "Reviewed", "Editor approved"]).map((t, i) => (
                <li key={i} className="flex items-center gap-3 text-sm">
                  <Check className="w-4 h-4 text-[#E53935]" strokeWidth={2} /> {t}
                </li>
              ))}
            </ul>
          </div>
        </div>

        {related.length > 0 && (
          <div className="mt-24">
            <p className="overline text-[#666]">More from {platformLabel(product.platform)}</p>
            <h2 className="font-display font-black text-3xl md:text-4xl tracking-tighter mt-3 mb-8">You might also like</h2>
            <div className="grid md:grid-cols-3 gap-6">
              {related.map((p) => <ProductCard key={p.id} product={p} />)}
            </div>
          </div>
        )}
      </section>

      <Footer />
    </div>
  );
}
